import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  ListSubheader,
  FormControl,
  InputLabel,
  Select,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  ImageList,
  ImageListItem,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import type { IndividualAnimal, AnimalGroup, AnimalYield } from '../../../../types/animal';

interface AnimalDialogProps {
  open: boolean;
  onClose: () => void;
  dialogMode: string;
  currentAnimal: Partial<IndividualAnimal> | null;
  handleInputChange: (field: keyof IndividualAnimal, value: any) => void;
  selectedAnimalType: string;
  handleAnimalTypeSelect: (type: string) => void;
  animalCategories: { category: string, types: { name: string }[] }[];
  availableBreeds: string[];
  customBreedName: string;
  handleCustomBreedInput: (value: string) => void;
  animalStatuses: string[];
  animalGroups: AnimalGroup[];
  isHybrid: boolean;
  setIsHybrid: (value: boolean) => void;
  selectedGroup: AnimalGroup | null;
  handlePhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSaveAnimal: () => void;
  handleAddYield: () => void;
  handleRemoveYield: (index: number) => void;
  handleYieldChange: (index: number, field: keyof AnimalYield, value: any) => void;
}

const AnimalDialog: React.FC<AnimalDialogProps> = ({
  open,
  onClose,
  dialogMode,
  currentAnimal,
  handleInputChange,
  selectedAnimalType,
  handleAnimalTypeSelect,
  animalCategories,
  availableBreeds,
  customBreedName,
  handleCustomBreedInput,
  animalStatuses,
  animalGroups,
  isHybrid,
  setIsHybrid,
  selectedGroup,
  handlePhotoUpload,
  handleSaveAnimal,
  handleAddYield,
  handleRemoveYield,
  handleYieldChange,
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        {dialogMode === 'edit' ? `Edit ${currentAnimal?.name}` : 'Add New Animal'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Name"
              value={currentAnimal?.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Tag ID"
              value={currentAnimal?.tagId || ''}
              onChange={(e) => handleInputChange('tagId', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Animal Type"
              value={selectedAnimalType}
              onChange={(e) => handleAnimalTypeSelect(e.target.value)}
              required
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                },
              }}
            >
              {animalCategories.map((category) => [
                <ListSubheader key={category.category}>
                  {category.category}
                </ListSubheader>,
                ...category.types.map((type) => (
                  <MenuItem key={type.name} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))
              ])}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Breed"
              value={currentAnimal?.breed || ''}
              onChange={(e) => handleInputChange('breed', e.target.value)}
              required
              select={availableBreeds.length > 0}
              disabled={availableBreeds.length === 0}
            >
              {availableBreeds.map((breed) => (
                <MenuItem key={breed} value={breed}>
                  {breed}
                </MenuItem>
              ))}
              <MenuItem value="Custom Breed">
                <em>Custom Breed (specify below)</em>
              </MenuItem>
            </TextField>
          </Grid>
          {currentAnimal?.breed === 'Custom Breed' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Custom Breed Name"
                value={customBreedName}
                onChange={(e) => handleCustomBreedInput(e.target.value)}
                placeholder="Enter a custom breed name"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Gender"
              value={currentAnimal?.gender || 'female'}
              onChange={(e) => handleInputChange('gender', e.target.value)}
              required
            >
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="male">Male</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Birth Date"
              value={currentAnimal?.birthDate || ''}
              onChange={(e) => handleInputChange('birthDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Status"
              value={currentAnimal?.status || 'healthy'}
              onChange={(e) => handleInputChange('status', e.target.value)}
              required
            >
              {animalStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Weight"
              value={currentAnimal?.weight || ''}
              onChange={(e) => handleInputChange('weight', Number(e.target.value))}
              InputProps={{
                endAdornment: <InputAdornment position="end">kg</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Asset Value (Ksh)"
              value={currentAnimal?.assetValue || ''}
              onChange={(e) => handleInputChange('assetValue', Number(e.target.value))}
              helperText="Intrinsic capital worth of the animal"
            />
          </Grid>

          {/* Dynamic Yields Section */}
          <Grid item xs={12}>
            <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Animal Yields</Typography>
              <Button 
                startIcon={<AddCircleOutlineIcon />} 
                size="small" 
                onClick={handleAddYield}
              >
                Add Yield Type
              </Button>
            </Box>
            {(!currentAnimal?.yields || currentAnimal.yields.length === 0) ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                No yields added yet.
              </Typography>
            ) : (
              currentAnimal.yields.map((yieldItem, index) => (
                <Box key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Yield Type (e.g. Milk, Eggs, Meat)"
                        value={yieldItem.yieldType}
                        onChange={(e) => handleYieldChange(index, 'yieldType', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Yield Unit (e.g. L, kg)"
                        value={yieldItem.yieldUnit}
                        onChange={(e) => handleYieldChange(index, 'yieldUnit', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Daily Avg Yield"
                        value={yieldItem.dailyAvgYield}
                        onChange={(e) => handleYieldChange(index, 'dailyAvgYield', Number(e.target.value))}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Market Price per Yield Unit"
                        value={yieldItem.marketPricePerUnit}
                        onChange={(e) => handleYieldChange(index, 'marketPricePerUnit', Number(e.target.value))}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        Est. Monthly: Ksh {(yieldItem.dailyAvgYield * yieldItem.marketPricePerUnit * 30).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton color="error" onClick={() => handleRemoveYield(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="parent-female-label">Mother</InputLabel>
              <Select
                labelId="parent-female-label"
                id="parent-female"
                value={currentAnimal?.parentFemaleId || ''}
                onChange={(e) => handleInputChange('parentFemaleId', e.target.value)}
                label="Mother"
              >
                <MenuItem value="">Unknown/External</MenuItem>
                {isHybrid ? (
                  // If hybrid, show dropdown to select group/type/breed for mother
                  [
                    <MenuItem key="disabled-header" disabled value="">Select Group/Type/Breed</MenuItem>,
                    ...animalGroups.map(group => (
                      <MenuItem key={group.id} value={group.id}>{group.type} - {group.breed}</MenuItem>
                    ))
                  ]
                ) : (
                  // If not hybrid, show only mothers from the same group
                  selectedGroup?.individuals
                    .filter(a => a.gender === 'female' && a.id !== currentAnimal?.id)
                    .map(a => (
                      <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                    ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="parent-male-label">Father</InputLabel>
              <Select
                labelId="parent-male-label"
                id="parent-male"
                value={currentAnimal?.parentMaleId || ''}
                onChange={(e) => handleInputChange('parentMaleId', e.target.value)}
                label="Father"
              >
                <MenuItem value="">Unknown/External</MenuItem>
                {isHybrid ? (
                  // If hybrid, show dropdown to select group/type/breed for father
                  [
                    <MenuItem key="disabled-header-male" disabled value="">Select Group/Type/Breed</MenuItem>,
                    ...animalGroups.map(group => (
                      <MenuItem key={group.id} value={group.id}>{group.type} - {group.breed}</MenuItem>
                    ))
                  ]
                ) : (
                  // If not hybrid, show only fathers from the same group
                  selectedGroup?.individuals
                    .filter(a => a.gender === 'male' && a.id !== currentAnimal?.id)
                    .map(a => (
                      <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                    ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!currentAnimal?.isBreeding}
                    onChange={(e) => handleInputChange('isBreeding', e.target.checked)}
                  />
                }
                label="Breeding Animal"
              />
            </FormGroup>
          </Grid>
          {currentAnimal?.status === 'pregnant' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Expected Delivery Date"
                value={currentAnimal?.expectedDeliveryDate || ''}
                onChange={(e) => handleInputChange('expectedDeliveryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={currentAnimal?.notes || ''}
              onChange={(e) => handleInputChange('notes', e.target.value)}
            />
          </Grid>
          
          {/* Photos section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Animal Photos
            </Typography>
            <Box sx={{ mt: 1, mb: 2 }}>
              {currentAnimal?.photos && currentAnimal.photos.length > 0 ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    {currentAnimal.photos.length} photo(s) attached. First photo will be used as profile picture.
                  </Typography>
                  <ImageList sx={{ maxHeight: 200, overflow: 'auto' }} cols={4} rowHeight={100}>
                    {currentAnimal.photos.map((photo, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={photo}
                          alt={`${currentAnimal?.name || 'Animal'} photo ${index + 1}`}
                          loading="lazy"
                          style={{ height: 100, objectFit: 'cover' }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No photos attached. Upload photos to help identify the animal.
                </Typography>
              )}
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="edit-animal-photo-upload"
                  type="file"
                  onChange={handlePhotoUpload}
                />
                <label htmlFor="edit-animal-photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AddPhotoAlternateIcon />}
                    size="small"
                  >
                    Add Photo
                  </Button>
                </label>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHybrid}
                  onChange={e => setIsHybrid(e.target.checked)}
                />
              }
              label="Hybrid"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSaveAnimal} 
          variant="contained" 
          color="primary"
          disabled={!currentAnimal?.name || !currentAnimal?.birthDate}
        >
          {dialogMode === 'edit' ? 'Update' : 'Add'} Animal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnimalDialog;
